/**
 * Admin → Reference Data.
 *
 * Mirrors the seats.aero vuerefdata payload into three master collections:
 *   - master_airports          (real airports + synthetic groupings)
 *   - master_operating_airlines (IATA → name + alliance)
 *   - master_transfer_partners  (issuer → seats.aero source edges)
 *
 * V1: read-only tables + paste-import. Admin opens the seats.aero URL in
 * a browser tab (Cloudflare blocks server-side fetches), copies the JSON
 * out, pastes it here, clicks Import. Full overwrite per import.
 */

import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import {
  RefreshCw, AlertCircle, ExternalLink, Upload, Search, CheckCircle2,
} from "lucide-react";

const VUEREFDATA_URL = "https://seats.aero/_api/vuerefdata";

const TABS = [
  { id: "airports",  label: "Airports" },
  { id: "airlines",  label: "Operating Airlines" },
  { id: "partners",  label: "Transfer Partners" },
];

export default function AdminRefData() {
  const [tab, setTab] = useState("airports");
  const [summary, setSummary] = useState(null);
  const [summaryErr, setSummaryErr] = useState("");

  const [pasteJson, setPasteJson] = useState("");
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [importErr, setImportErr] = useState("");

  async function refreshSummary() {
    setSummaryErr("");
    try {
      const { data } = await api.get("/admin/master-list/refdata/summary");
      setSummary(data);
    } catch (e) {
      setSummaryErr(e?.response?.status === 403 ? "Admin access required." : (e?.message || "load failed"));
    }
  }

  useEffect(() => { refreshSummary(); }, []);

  async function doImport() {
    setImporting(true); setImportMsg(""); setImportErr("");
    let payload;
    try {
      payload = JSON.parse(pasteJson);
    } catch (e) {
      setImportErr("Pasted text is not valid JSON.");
      setImporting(false);
      return;
    }
    try {
      const { data } = await api.post("/admin/master-list/refdata/import", payload);
      setImportMsg(
        `Imported ${data.airports} airports · ${data.operating_airlines} airlines · ` +
        `${data.transfer_partners} transfer routes · ${data.programs_updated} programs updated`,
      );
      setPasteJson("");
      await refreshSummary();
    } catch (e) {
      setImportErr(e?.response?.data?.detail || e?.message || "import failed");
    } finally {
      setImporting(false);
    }
  }

  return (
    <AdminLayout activeTab="refdata" title="Reference Data">
      <div data-testid="admin-refdata-page">
        <div className="section-eyebrow">Admin · Reference data</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">
          seats.aero reference data
        </h1>
        <p className="text-[13.5px] text-neutral-600 mt-2 max-w-[780px]">
          Airports, operating airlines, and transfer-partner edges sourced
          from seats.aero's vuerefdata payload. Each import fully overwrites
          the three collections below.
        </p>

        {/* Summary cards */}
        <div className="flex flex-wrap gap-3 mt-6 text-[12.5px]">
          <div className="card px-4 py-2 text-neutral-700">
            Airports <span className="font-semibold tabular ml-1">{summary?.airports?.total ?? "—"}</span>
            <span className="text-neutral-400 ml-2">
              ({summary?.airports?.real ?? "—"} real, {summary?.airports?.groups ?? "—"} groups)
            </span>
          </div>
          <div className="card px-4 py-2 text-neutral-700">
            Operating airlines <span className="font-semibold tabular ml-1">{summary?.operating_airlines?.total ?? "—"}</span>
          </div>
          <div className="card px-4 py-2 text-neutral-700">
            Award programs (seats.aero sources) <span className="font-semibold tabular ml-1">{summary?.award_programs?.total ?? "—"}</span>
          </div>
          <div className="card px-4 py-2 text-neutral-700">
            Transfer partner routes <span className="font-semibold tabular ml-1">{summary?.transfer_partners?.total ?? "—"}</span>
          </div>
          {summary?.last_imported_at && (
            <div className="card px-4 py-2 text-neutral-500">
              Last import <span className="font-mono ml-1">{new Date(summary.last_imported_at).toLocaleString()}</span>
            </div>
          )}
        </div>

        {summaryErr && (
          <div className="mt-4 text-[12px] text-red-600 inline-flex items-center gap-1.5">
            <AlertCircle size={12} /> {summaryErr}
          </div>
        )}

        {/* Import card */}
        <div className="card p-6 mt-6">
          <div className="font-display font-bold text-[15px]">Import vuerefdata</div>
          <p className="text-[12.5px] text-neutral-600 mt-1.5 max-w-[780px]">
            Open the link below in a browser (Cloudflare blocks server-side
            fetches), copy the entire JSON response, paste it here, and
            click Import. This is a <span className="font-semibold">full overwrite</span> —
            all rows in the 3 collections are replaced.
          </p>

          <div className="mt-4">
            <a
              href={VUEREFDATA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost text-[12.5px] inline-flex items-center gap-1.5"
              data-testid="refdata-vue-link"
            >
              <ExternalLink size={13} /> Open seats.aero vuerefdata
              <span className="text-neutral-400 ml-1 font-mono text-[11px]">{VUEREFDATA_URL}</span>
            </a>
          </div>

          <textarea
            value={pasteJson}
            onChange={(e) => setPasteJson(e.target.value)}
            placeholder='Paste the full JSON response here (starts with `{"sources": …`)'
            rows={8}
            className="input mt-4 font-mono text-[11px] w-full"
            data-testid="refdata-paste-textarea"
          />

          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={doImport}
              disabled={importing || !pasteJson.trim()}
              className="btn btn-primary text-[12.5px] inline-flex items-center gap-1.5"
              data-testid="refdata-import-btn"
            >
              {importing ? <><span className="spinner" /> Importing…</> : <><Upload size={13} /> Import (overwrite)</>}
            </button>
            {importMsg && (
              <span className="text-[12px] text-green-700 inline-flex items-center gap-1.5">
                <CheckCircle2 size={12} /> {importMsg}
              </span>
            )}
            {importErr && (
              <span className="text-[12px] text-red-600 inline-flex items-center gap-1.5">
                <AlertCircle size={12} /> {importErr}
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8">
          <div className="flex gap-1.5 border-b border-black/5" data-testid="refdata-subtabs">
            {TABS.map((t) => {
              const active = t.id === tab;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-3 py-2 text-[13px] font-semibold border-b-2 -mb-[1px] transition-colors ${
                    active ? "border-black text-black" : "border-transparent text-neutral-500 hover:text-black"
                  }`}
                  data-testid={`refdata-tab-${t.id}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          <div className="mt-4">
            {tab === "airports"  && <AirportsTab />}
            {tab === "airlines"  && <AirlinesTab />}
            {tab === "partners"  && <PartnersTab />}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

// ---------------------------------------------------------------------------
// Airports tab (paginated + kind filter + iata/name search)
// ---------------------------------------------------------------------------
function AirportsTab() {
  const [q, setQ]         = useState("");
  const [kind, setKind]   = useState("");
  const [rows, setRows]   = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr]     = useState("");
  const LIMIT = 100;

  async function load(nextSkip = 0) {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/master-list/refdata/airports", {
        params: { q, kind, skip: nextSkip, limit: LIMIT },
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setSkip(nextSkip);
    } catch (e) {
      setErr(e?.message || "load failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(0); /* eslint-disable-next-line */ }, [q, kind]);

  const showingFrom = total === 0 ? 0 : skip + 1;
  const showingTo   = Math.min(skip + LIMIT, total);

  return (
    <div data-testid="refdata-airports-tab">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="IATA, name, city…"
            className="input pl-7 text-[12.5px] w-[260px]"
          />
        </div>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="input text-[12.5px]">
          <option value="">All kinds</option>
          <option value="airport">Airports</option>
          <option value="group">Groups (NYC/EUR/…)</option>
        </select>
        <div className="ml-auto text-[12px] text-neutral-500 tabular">
          {loading ? "Loading…" : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </div>
      </div>

      {err && <div className="text-[12px] text-red-600">{err}</div>}

      <div className="card overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="bg-black/[0.02] text-neutral-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">IATA</th>
              <th className="text-left px-3 py-2 font-semibold">Kind</th>
              <th className="text-left px-3 py-2 font-semibold">Name</th>
              <th className="text-left px-3 py-2 font-semibold">City</th>
              <th className="text-left px-3 py-2 font-semibold">Country</th>
              <th className="text-left px-3 py-2 font-semibold">Region</th>
              <th className="text-left px-3 py-2 font-semibold">Members</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-neutral-400">No airports.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.iata} className="border-t border-black/5 hover:bg-black/[0.015]">
                <td className="px-3 py-1.5 font-mono font-semibold">{r.iata}</td>
                <td className="px-3 py-1.5">
                  {r.kind === "group" ? (
                    <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10.5px]">group</span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded bg-black/5 text-neutral-600 text-[10.5px]">airport</span>
                  )}
                </td>
                <td className="px-3 py-1.5">{r.name || "—"}</td>
                <td className="px-3 py-1.5 text-neutral-600">{r.city || "—"}</td>
                <td className="px-3 py-1.5 text-neutral-600">{r.country || "—"}</td>
                <td className="px-3 py-1.5 text-neutral-500">{r.region || "—"}</td>
                <td className="px-3 py-1.5 font-mono text-[10.5px] text-neutral-500">
                  {(r.member_iatas || []).join(", ") || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > LIMIT && (
        <div className="mt-3 flex items-center gap-2 text-[12px]">
          <button
            onClick={() => load(Math.max(0, skip - LIMIT))}
            disabled={skip === 0 || loading}
            className="btn btn-ghost text-[12px]"
          >Prev</button>
          <button
            onClick={() => load(skip + LIMIT)}
            disabled={skip + LIMIT >= total || loading}
            className="btn btn-ghost text-[12px]"
          >Next</button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Operating airlines tab (~90 rows, no pagination needed)
// ---------------------------------------------------------------------------
function AirlinesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true); setErr("");
      try {
        const { data } = await api.get("/admin/master-list/refdata/operating-airlines");
        setRows(data.rows || []);
      } catch (e) {
        setErr(e?.message || "load failed");
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      (r.iata || "").toLowerCase().includes(needle) ||
      (r.name || "").toLowerCase().includes(needle) ||
      (r.alliance || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  return (
    <div data-testid="refdata-airlines-tab">
      <div className="flex items-center gap-2 mb-3">
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="IATA, name, alliance…"
            className="input pl-7 text-[12.5px] w-[260px]"
          />
        </div>
        <div className="ml-auto text-[12px] text-neutral-500 tabular">
          {loading ? "Loading…" : `${filtered.length} of ${rows.length}`}
        </div>
      </div>
      {err && <div className="text-[12px] text-red-600 mb-2">{err}</div>}
      <div className="card overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="bg-black/[0.02] text-neutral-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Airline</th>
              <th className="text-left px-3 py-2 font-semibold">Alliance</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={2} className="px-3 py-6 text-center text-neutral-400">No airlines.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.iata} className="border-t border-black/5 hover:bg-black/[0.015]">
                <td className="px-3 py-1.5">
                  {r.name}{" "}
                  <span className="font-mono text-[11px] text-neutral-400">({r.iata})</span>
                </td>
                <td className="px-3 py-1.5 text-neutral-600">{r.alliance || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transfer partners tab (flat edge list, grouped by from_code)
// ---------------------------------------------------------------------------
function PartnersTab() {
  const [rows, setRows] = useState([]);
  const [fromNames, setFromNames] = useState({});  // master_programs.code -> name
  const [awardBySlug, setAwardBySlug] = useState({}); // seats.aero slug -> {name, iata}
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true); setErr("");
      try {
        const [edges, progs, awards] = await Promise.all([
          api.get("/admin/master-list/refdata/transfer-partners"),
          api.get("/admin/master-list"),
          api.get("/admin/master-list/refdata/award-programs"),
        ]);
        setRows(edges.data?.rows || []);
        const fromMap = {};
        for (const p of (progs.data?.programs || [])) fromMap[p.code] = p.name;
        setFromNames(fromMap);
        const awardMap = {};
        for (const a of (awards.data?.rows || [])) awardMap[a.slug] = { name: a.name, iata: a.iata };
        setAwardBySlug(awardMap);
      } catch (e) {
        setErr(e?.message || "load failed");
      } finally { setLoading(false); }
    })();
  }, []);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      if (!m.has(r.from_code)) m.set(r.from_code, []);
      m.get(r.from_code).push(r);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  const fmtFrom = (code) => {
    const name = fromNames[code];
    return name
      ? <><span>{name}</span> <span className="text-neutral-400 font-mono text-[11px]">({code})</span></>
      : <span className="font-mono">{code}</span>;
  };

  const fmtTo = (slug) => {
    const a = awardBySlug[slug];
    if (!a) return <span className="font-mono">{slug}</span>;
    const tail = a.iata ? `${slug} · ${a.iata}` : slug;
    return <><span>{a.name}</span> <span className="text-neutral-400 font-mono text-[11px]">({tail})</span></>;
  };

  return (
    <div data-testid="refdata-partners-tab">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-[12px] text-neutral-500 tabular">
          {loading ? "Loading…" : `${rows.length} routes across ${grouped.length} programs`}
        </div>
      </div>
      {err && <div className="text-[12px] text-red-600 mb-2">{err}</div>}
      <div className="space-y-4">
        {grouped.length === 0 && !loading && (
          <div className="card p-6 text-center text-[12.5px] text-neutral-400">
            No transfer partner routes yet. Import vuerefdata to populate.
          </div>
        )}
        {grouped.map(([fromCode, routes]) => (
          <div key={fromCode} className="card overflow-hidden">
            <div className="px-4 py-2 bg-black/[0.02] font-semibold text-[12.5px]">
              {fmtFrom(fromCode)}
              <span className="text-neutral-400 font-normal ml-2">→ {routes.length} partners</span>
            </div>
            <table className="w-full text-[12.5px]">
              <thead className="text-neutral-500 text-[11px] uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-1.5 font-semibold">To (seats.aero source)</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Ratio</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {routes.map((r) => (
                  <tr key={r.from_code + ":" + r.to_code} className="border-t border-black/5">
                    <td className="px-3 py-1.5">{fmtTo(r.to_code)}</td>
                    <td className="px-3 py-1.5 tabular">{r.ratio || "—"}</td>
                    <td className="px-3 py-1.5 text-neutral-500">
                      {r.active ? "active" : <span className="text-red-600">inactive</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
